<template>

  <ion-modal
      :is-open="showModal"
      :backdrop-dismiss="false"
      :swipe-to-close="false"
      :css-class="['auto-height blurred']">
    <div class="monitoring-camera-modal ion-no-padding">
      <ion-row>
        <ion-col>
          <Banner
              v-if="isCameraConnected===false"
              :display-icon="true"
              :message="modalMessage"
              banner-type="warning"
              style="margin-bottom: 15px;"/>
        </ion-col>
      </ion-row>
      <ion-row class="drop-shadow-row">
        <ion-col size="12">
          <div id="camera-setup-video-wrapper">
          </div>
          <div id="detection-error-wrapper" v-if="!initial_detection &&detectedFaceResults.count !== 1">
            <span v-if="detectedFaceResults.count == 0">{{ language.no_face_detected }}</span>
            <span v-else-if="detectedFaceResults.count > 1 ">{{ language.multiple_face_detected }}</span>
          </div>
        </ion-col>
        <ion-col size="12">
          <CameraDevice v-if="isCameraConnected && cameraPermissionRejected === false"/>
        </ion-col>
      </ion-row>
      <ion-row class="modal-actions">
        <ion-button v-if="isCameraConnected && cameraPermissionRejected === false "
                    class="modal-close-button"
                    :disabled="(detectedFaceResults.count !== 1)"
                    @click="closeModal">
          {{ language.link_start_test }}
        </ion-button>
        <ion-button v-else class="modal-close-button" @click="refreshBrowser()">{{
            language.refresh_browser
          }}
        </ion-button>
      </ion-row>
    </div>
  </ion-modal>

  <div style="display:block; position:absolute;  left: -99999px;">
    <video id="camVideo" ref="camVideo" autoplay muted playsinline
           style=" z-index: -99999;
    position: absolute;"></video>

    <div>
      <canvas id="cmVideoLiveView" ref="videoCanvas" class="no-camera"></canvas>
    </div>
  </div>


</template>

<script setup>
import {computed, getCurrentInstance, reactive, ref, watch} from "vue";
import {templateRef} from "@vueuse/core";
import store from "@/store";
import {CmFaceDetector} from "@/core/useFaceDetection/face_detector";
import {useMonitorSnapshot} from "@/core/useMonitor/snapshot";
import constant from "@/constant";
import {MonitorLoggingEvent, useMonitorLogging} from "@/core/useMonitor/logging";
import Banner from "@/components/html/Banner.vue";
import {IonButton, IonCol, IonModal, IonRow} from "@ionic/vue";
import CameraDevice from "@/components/test/CameraDevice.vue";
import {NoDevicePermissionError} from "@/errors/NoDevicePermissionError";
import {CameraPermissionDeniedError} from "@/errors/CameraPermissionDeniedError";
import global_methods from "@/utils/globalFunctions";

const instance = getCurrentInstance();
const globalEmitter = instance.appContext.config.globalProperties.$globalEmitter;

// camera video (for webcam recording)
const camVideo = templateRef("camVideo")

// video canvas for live feed (showing in left-right inverted frame)
const videoCanvas = templateRef('videoCanvas')

// language set
const language = store.getters.languageSet;

const showModal = ref(false)
const videoFeedWidth = 64;
const videoFeedHeight = 64;
const mobileFeedWidthHeight = 36;

const state = reactive({
  detection_error_text: "",
  camera_started: false,
  test_state: store.getters.testState,
  camera_disconnected: false, // set true when camera become disconnected
});

const {
  logNoFaceDetected, logMultipleFaceDetected, logCameraRestore, logCameraLost, logPhotoUploadFailed
} = useMonitorLogging();


// loading the image capture and submit library
const {
  capture: captureImage,
  submitImage: submitImage,
} = useMonitorSnapshot(camVideo, videoCanvas);

const cameraPermissionRejected = ref(false);
const cameraPermission = computed(() => {
  return store.state.Monitoring.cameraPermissionStatus
})

let faceDetector = null


watch(cameraPermission, (newValue, oldValue) => {
  // console.log({oldValue:oldValue,newValue:newValue} );
  if (newValue === 'denied' || newValue === 'prompt') {

    store.dispatch("Monitoring/setCameraDisconnected");

    //show Permission setup warning modal.
    switch (store.getters.testState) {
      case constant.STATUS_TEST_INTRO:
      case constant.STATUS_TEST_IN_PROGRESS:

        if (store.state.Monitoring.cameraMonitoringStarted) {
          __stopTestDueToDeviceOrPermissionIssue();
        } else {
          cameraPermissionRejected.value = true;
          faceDetector.stop(true);
          faceDetector.clearFrame();
        }

        globalEmitter.emit('camera-permission-denied');

        break;
    }
  }
})

globalEmitter.on('stop-camera', async () => {
  if (showModal.value === true) {
    showModal.value = false;
  }
  if (faceDetector !== null) {
    faceDetector.stop(true);
  }

})

globalEmitter.on('start-camera', async () => {

  try {
    faceDetector = new CmFaceDetector(camVideo, videoCanvas);
    await faceDetector.setCameraPermissionState();
    await faceDetector.setCameraList();
    state.camera_started = true;
  } catch (error) {
    if (error instanceof NoDevicePermissionError) {
      globalEmitter.emit('no-device-permission');

    } else if (error instanceof CameraPermissionDeniedError) {
      cameraPermissionRejected.value = true;
      globalEmitter.emit('camera-permission-denied');
    }

  }

  try {

    switch (store.getters.testState) {

      case constant.STATUS_TEST_INTRO:
        await __startCamera("intro-video");
        break;

      case constant.STATUS_TEST_IN_PROGRESS:
        showModal.value = true
        await __startCamera("camera-setup-video-wrapper");
        break;
    }

  } catch (error) {
    if (error instanceof NoDevicePermissionError) {
      globalEmitter.emit('no-device-permission');

    } else if (error instanceof CameraPermissionDeniedError) {
      __hideCameraLoading();
      faceDetector.stop();
      faceDetector.clearFrame();
      cameraPermissionRejected.value = true;
      globalEmitter.emit('camera-permission-denied');
    }
  }
})

const __stopTestDueToDeviceOrPermissionIssue = () => {

  logCameraLost()

  // camera_disconnected_after_test_started = true;
  cameraPermissionRejected.value = true;
  showModal.value = true;
  faceDetector.stop(true);
  faceDetector.clearFrame();
  store.dispatch("Monitoring/setCameraDisconnected");
  store.dispatch('Monitoring/haltMonitoring');
  __setVideoFeedLocation('camera-setup-video-wrapper')

}

const __startCamera = async (videoFeedTargetDivId) => {

  __setVideoFeedLocation(videoFeedTargetDivId);

  if (!cameraPermissionRejected.value) {
    __showCameraLoading()
    await faceDetector.start();

    store.dispatch("Monitoring/setCameraConnected");

    setTimeout(() => {
      __hideCameraLoading();
    }, 3000)// add enough time to remove loading spinner behinder scene
  }
}
globalEmitter.on("feed-location", (targetDivId) => {
  __setVideoFeedLocation(targetDivId, mobileFeedWidthHeight, mobileFeedWidthHeight);
  let feedWrapper = document.querySelector('#videoFeedWrapper')
  feedWrapper.style.zIndex = -999999;
})

globalEmitter.on('capture-intro-image', () => {
  captureImage().then((image) => {
    globalEmitter.emit('intro-image-captured', image);
  })
})

globalEmitter.on(constant.EVENT_SUBMIT_PHOTO, (data) => {
  switch (data.type) {
    case constant.PHOTO_INTRO:
      submitImage(constant.PHOTO_INTRO, data.image).then(() => {
        store.dispatch('Monitoring/setIntroImageUploaded')
        let setVideoId= setInterval(()=>{
          /**
           * Once photo is submitted from intro page, there is a gap to rendering test page from Camera Intro page
           *
           * As we move the test live feed from App Area to Test page (test content), we will need to ensure
           * videoFeedWrapper is rendered in the app after photo is submitted. Below code will do that.
           *
           */

          let videoFeedWrapper = document.querySelector('#videoFeedWrapper');
          if(videoFeedWrapper !== null) {
            clearInterval(setVideoId);
            __setVideoFeedLocation("videoFeedWrapper", videoFeedWidth, videoFeedHeight);
            showVideoFeed();
          }
        })

      }).catch((error) => {
        globalEmitter.emit('error-on-intro-photo-uploading', error);
      })
      break;

    case constant.PHOTO_IN_PROGRESS_STATIC:
      captureImage().then((image) => {
        if (image) {
          submitImageWithRetry(image, 'in-progress');
        }
      });
      break;

    case constant.PHOTO_COMPLETE:
      if (store.getters["Monitoring/didStartUploadCompleteImage"] === false) {
        store.dispatch('Monitoring/startCompleteImageUpload');


        // we need to wait to send an image until camera is connected (in case of refresh)
        let imageUploadIntervalId = setInterval(() => {
          if (isCameraConnected.value) {
            store.dispatch('Monitoring/setIsUploadingImage', true);
            clearInterval(imageUploadIntervalId);

            captureImage().then((image) => {
              // Once image is captured, we can stop face detector and video feed.
              faceDetector.stop(true);
              videoCanvas.value.style.display = "none";
              hideVideoFeed();

              if (image) {

                submitImageWithRetry(image, 'complete').then(() => {
                  // console.log('success upload photo')
                }).catch(() => {
                  // console.log('fail upload photo')
                }).finally(() => {
                  // console.log('finially upload photo process')

                  /**
                   * Once the user clicks the finish button, we want to ensure the test completed
                   * and show the result regardless of success of the uploading last photo.
                   * To do that, below lines are located in finally() section instead of then() section.
                   */

                  store.dispatch('Monitoring/setIsUploadingImage', false);
                  store.dispatch('Monitoring/setCompleteImageUploaded', true);

                  if (showModal.value == true) {
                    showModal.value = false;
                  }
                })
              }
            });
          }
        }, 1000);
      }
      break;

  }
})

const modalMessage = computed(() => {

  if (cameraPermissionRejected.value === false || state.camera_disconnected === true) {
    return language['test_monitoring_camera_disconnect_message']
  } else {
    return language['camera_no_permission']
  }
})


const closeModal = () => {
  const isMobileDevice = store.getters.isMobileDevice;

  if (isMobileDevice) {
    let videoFeedWrapper = document.querySelector("#videoFeedWrapper");
    videoFeedWrapper.style.display = "none";
    __setVideoFeedLocation('top-camera-feed-wrapper', mobileFeedWidthHeight, mobileFeedWidthHeight)
  } else {
    __setVideoFeedLocation("videoFeedWrapper", videoFeedWidth, videoFeedHeight);
  }

  showVideoFeed();

  showModal.value = false;
  logCameraRestore();

  store.dispatch('Monitoring/setCameraMonitoringStart', true);

  if (store.getters["Monitoring/isMonitoringBrowserEnabled"]) {
    store.dispatch('Monitoring/setUserStartBrowserMonitoring', true)
    globalEmitter.emit('start-browser-monitoring');
  }


  setTimeout(() => {
    store.dispatch('Monitoring/unHaltMonitoring');
  }, 1000)

};

const showVideoFeed = () => {
  let feedWrapper = document.querySelector('#videoFeedWrapper')
  feedWrapper.style.zIndex = 2;
}

const hideVideoFeed = () => {

  let feedWrapper = document.querySelector('#videoFeedWrapper')
  feedWrapper.style.zIndex = -999999;
}

const refreshBrowser = () => {
  window.location.reload();
}

const __showCameraLoading = () => {
  videoCanvas.value.classList.add("loading-camera");
}

const __hideCameraLoading = () => {
  videoCanvas.value.classList.remove("loading-camera");
}


const __setVideoFeedLocation = (targetDivId, width = null, height = null, count = 0) => {

  let targetDiv = document.querySelector("#" + targetDivId);

  if (targetDiv === null && count < 100) {
    setTimeout(() => {
      __setVideoFeedLocation(targetDivId, width, height, count++)
    }, 300)
  } else {
    let targetRect = targetDiv.getBoundingClientRect();
    targetDiv.appendChild(videoCanvas.value);


    if (width == null || height == null) {
      faceDetector.setLiveViewDimension(targetRect.width, targetRect.height)
    } else {
      faceDetector.setLiveViewDimension(width, height)
    }

    videoCanvas.value.style.position = 'relative';
    videoCanvas.value.style.display = 'block';
  }
}


/** Face detection algorithm ******************
 * When we detect face does not exist or multiple face exsit,
 * We need to take a capture from the selected webcam.
 * Also log to the server when
 *  1. With Alert: the user clicks continue taking test button
 *  2. Without Alert: the user's face is detected again
 * *******************************************/



// Face detection algorithm
let initial_detection = ref(true);
let last_issue_timestamp = null;
let halt_detection = false;
let monitoring_paused = computed(() => store.getters['Monitoring/isMonitoringHalted']);
// let camera_disconnected_after_test_started = false;
const detectedFaceResults = computed(() => store.getters['Monitoring/getFaceDetectionState']);
const cameraDevices = computed(() => {
  return store.state.Monitoring.cameraDevices
});

watch(monitoring_paused, (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    // detect face detection
    // restart face detection
    __resetMonitoring();
  }
})

watch(cameraDevices, (newValue, oldValue) => {

  if (newValue.length < oldValue.length) {

    if (store.getters.testState === constant.STATUS_TEST_INTRO && newValue.length === 0) {
      faceDetector.stop(true);
      faceDetector.clearFrame();
      store.dispatch("Monitoring/setCameraDisconnected");
      __setVideoFeedLocation('camera-setup-video-wrapper')

    } else if (store.getters.testState === constant.STATUS_TEST_IN_PROGRESS) {
      let usedCameraId = store.getters['Monitoring/cameraId'];
      let cameraFound = false;
      newValue.forEach(camera => {
        if (camera.deviceId === usedCameraId) {
          cameraFound = true;
        }
      })

      if (!cameraFound) {
        console.debug('camera disconnected')
        state.camera_disconnected=true;
        // if camera is disconnected during the test, we need to halt monitoring
        if (store.state.Monitoring.cameraMonitoringStarted) {
          __stopTestDueToDeviceOrPermissionIssue();
        } else {

          cameraPermissionRejected.value = true;
          faceDetector.stop(true);
          faceDetector.clearFrame();
        }
      }
    }

  }
})


// when proctor_camera_mode is on, start doing the face detection
if (store.getters["Monitoring/isMonitoringCameraEnabled"]) {
  // console.debug('face detection start')
  watch(detectedFaceResults, (detectionResults) => {

    if (store.state.Test.settings.test.proctor_camera_mode && store.getters.testState === constant.STATUS_TEST_IN_PROGRESS) {

      // we need to skip very initial detection as it's count is always 0;
      if (initial_detection.value) {
        initial_detection.value = false;
        return;
      }

      if (showModal.value === false) {

        if (monitoring_paused.value === false) {

          //DETECT NO FACE - face count 0
          if (detectionResults.count == 0) {
            __setFaceDetectionEventWithWarning(constant.ERROR_NO_FACE_DETECTED)

            //DETECT NO FACE - face coun 1, but low confidence
          } else if (detectionResults.count == 1 && detectionResults.results[0].confidence < 55) {
            __setFaceDetectionEventWithWarning(constant.ERROR_NO_FACE_DETECTED)

            //DETECT ONE FACE - We need to restore from the previous event
          } else if (detectionResults.count == 1 && detectionResults.results[0].confidence > 55 && last_issue_timestamp !== null) {
            __resetMonitoring();

            // DETECT MULTIPLE FACES
          } else if (detectionResults.count > 1) {
            let detected_scores = 0;
            detectionResults.results.forEach((single_result) => {
              detected_scores += single_result.confidence;
            })

            if (detected_scores / detectionResults.count > 85) { // average detection confidence is over 85, then we call it multiple face detected
              __setFaceDetectionEventWithWarning(constant.ERROR_MULTIPLE_FACE_DETECTED)
            }
          }
        }
      }
    }
  });
} else {
  // console.log('No face detection')
}

function submitImageWithRetry(image, test_state, event_id = null, face_anomaly_event_log_type = null, num_try = 1) {
  return submitImage(test_state, image, event_id).then((response) => {

    return response
  }).catch((err) => {
    if (num_try <= 3) {

      return submitImageWithRetry(image, test_state, event_id, face_anomaly_event_log_type, ++num_try)
    } else {

      logPhotoUploadFailed(test_state, face_anomaly_event_log_type);
      return Promise.reject(err);
    }
  })

}


const isCameraConnected = computed(() => {
  return store.getters['Monitoring/isCameraConnected'];
})


globalEmitter.on('camera-change', (cameraId) => {

  faceDetector.stop().then(()=>{
    store.dispatch('Monitoring/setCameraConnected')
    faceDetector.playVideo(cameraId)
  });

});


function __resetMonitoring() {
  last_issue_timestamp = null;
  halt_detection = false;
  global_methods.fadeInTestContent();
  store.dispatch('Monitoring/setNoFaceDetected', false);
}

function __setFaceDetectionEventWithWarning(event_type) {

  let current_time = Math.floor(Date.now() / 1000); // timestamp in second

  // second delay before showing warning from the initial detection time.
  let warning_delay_sec = 2;
  if(event_type === constant.ERROR_NO_FACE_DETECTED){
    warning_delay_sec = 5;
  }


  if (halt_detection == false) {
    last_issue_timestamp = current_time;
    halt_detection = true;

    if(event_type == constant.ERROR_NO_FACE_DETECTED){
      global_methods.fadeOutTestContent();

      store.dispatch('Monitoring/setNoFaceDetected', true);
    }
  } else if (halt_detection === true && (current_time - last_issue_timestamp) >= warning_delay_sec) {


    // set event type for log
    let event_log_type = null;

    // temporary variable that stores log function
    let log_function = null;
    if (event_type != null) {
      switch (event_type) {
        case constant.ERROR_NO_FACE_DETECTED:
          event_log_type = MonitorLoggingEvent.FACE_LOST;
          log_function = logNoFaceDetected;
          break;
        case constant.ERROR_MULTIPLE_FACE_DETECTED:
          event_log_type = MonitorLoggingEvent.MULTIPLE_FACE_DETECT;
          log_function = logMultipleFaceDetected;
          break;
      }
    }

    //Error type - show error
    let error = {};
    error.type = 'error';
    error.message = language[event_type];
    error.error_code = event_type;

    store.dispatch('Monitoring/haltMonitoring', error);


    let photo_allowed_count = store.state.Monitoring.photoState.in_progress_anomaly_photo_allowed;
    let photo_taken_count = store.state.Monitoring.photoState.in_progress_anomaly_photo_taken;

    // when photo allowed count is smaller or equal than photo taken, then we just log the error
    if (photo_allowed_count <= photo_taken_count) {

      log_function();

    } else {

      //other wise upload image
      captureImage().then((image) => {

        if (image) {
1
          store.dispatch('Monitoring/setIsUploadingImage', true);
          log_function().then((event_id) => {
            submitImageWithRetry(image, 'in-progress', event_id, event_log_type).finally(() => {
              store.dispatch('Monitoring/setIsUploadingImage', false);
            })

          }) // log_function().then()
        } // if (image)
      }) // captureImage().then(image)
    }
  }
}


</script>

<style lang="scss" scoped>
ion-button[shape=circle] {
  --border-radius: 50%;
  width: 64px;
  height: 64px;
}

#camera-setup-video-wrapper {
  width: 100%;
  aspect-ratio: 1/1;
}


ion-modal.blurred {
  backdrop-filter: blur(10px);
}

ion-modal.auto-height {
  --height: auto;
}

ion-modal.auto-height .ion-page {
  position: relative;
  display: block;
  contain: content;
}

ion-modal.auto-height .ion-page .inner-content {
  max-height: 80vh;
  overflow: auto;
}

.monitoring-camera-modal {
  /*background-color: $color-yellow-bg !important;*/
  padding: 15px;
}

.drop-shadow-row {
  display: flex;
  padding: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-radius: 10px;
  border: 1px solid #E9ECEF;
  background: #FFFFFF;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.04);
}

.modal-content {
  padding-left: 15px;
  padding-right: 15px;
}

.modal-icon .icon {
  border: 0;
  width: 45px;
  height: 45px;
  margin: 48.5px auto auto;
  background-size: 45px;
}

.modal-message {
  min-width: fit-content;
  max-width: 399px;
  margin: 36px auto;
  font-weight: bold;
}

.modal-actions {
  background-color: variables.$color-white;
  height: 84px;
  padding-top: 20px;
  padding-bottom: 16px;
}

.modal-close-button {
  margin: auto;
  --background: variables.$color-white;
  border: 1px solid #CCD3DD;
  border-radius: 4px;
  --color: #282B30;
  --background-hover: #454F59;
}




#cmVideoLiveView {
  background-color: variables.$color-gray-02;
  width: 100%;
  aspect-ratio: 1/1;
  background-position: center center;
  background-repeat: no-repeat;

  &.no-camera {
    background-image: url("@/assets/icons/no-camera.svg");
  }

  &.loading-camera {
    background-image: url("/public/static/images/loading-spinner.svg");
    background-size: 41px;
  }
}

#detection-error-wrapper {
  display: inline-flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #FFE073;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  bottom: 20px;
}
</style>